import { AlertTriangle, Clock, FileText, MessageSquare, PauseCircle, Star } from 'lucide-react'
import type { CockpitCard } from '../../types/cockpit.types'
import { STATUS_PUNKT, formatStunden, kartenRand, kurzDatum } from './cockpit-labels'

/**
 * AP-35 Detailblatt 11 4.1 — **die Karte.**
 *
 * Kompakt (≈ 260 × 150), Rand nach Status; „klein" zeigt nur Kennzeile und
 * Einteilung. Die Karte ist der Arbeitsschritt — nicht die Position (12.2-1).
 */
export function CockpitCardView({
  karte,
  klein = false,
  gewaehlt = false,
  onEinteilen,
}: {
  karte: CockpitCard
  klein?: boolean
  gewaehlt?: boolean
  onEinteilen?: () => void
}) {
  const laufend = karte.assignments.filter((a) => a.current)
  return (
    <div
      className={`flex h-full w-full flex-col gap-1 overflow-hidden rounded-md border-2 bg-card p-2 text-xs shadow-sm ${kartenRand(
        karte.executionStatus,
        karte.overdue,
      )} ${gewaehlt ? 'ring-2 ring-primary ring-offset-1' : ''}`}
    >
      {!klein && (
        <div className="flex items-center gap-1.5 text-muted-foreground">
          <span className={`inline-block h-2 w-2 shrink-0 rounded-full ${STATUS_PUNKT[karte.executionStatus] ?? 'bg-muted'}`} />
          <span className="truncate">
            {karte.project.projectNumber} {karte.project.name}
          </span>
        </div>
      )}
      <div className="truncate font-semibold text-foreground" title={`${karte.position.number} ${karte.position.name}`}>
        {karte.position.number} {karte.position.name}
      </div>
      <div className="truncate text-foreground">
        <span className="font-mono text-muted-foreground">{karte.number}</span> {karte.activityName}
      </div>

      {!klein && (
        <>
          <div className="flex items-center gap-2 tabular-nums text-muted-foreground">
            <span>
              {formatStunden(karte.plannedHoursTotal)} / {formatStunden(karte.actualHours)} h
            </span>
            {karte.progressPercent != null && (
              <span className="h-1.5 flex-1 overflow-hidden rounded bg-muted">
                <span className="block h-full bg-primary" style={{ width: `${karte.progressPercent}%` }} />
              </span>
            )}
            {karte.dueAt && (
              <span className={`ml-auto flex items-center gap-0.5 ${karte.overdue ? 'font-semibold text-destructive' : ''}`}>
                {karte.fixedAt && <Clock className="h-3 w-3" aria-label="fixiert" />}
                {kurzDatum(karte.dueAt)}
              </span>
            )}
          </div>

          <div className="flex flex-wrap gap-1">
            {karte.marks.notReleased && (
              <span className="rounded bg-muted px-1 text-muted-foreground" title={karte.releaseNote ?? undefined}>
                nicht freigegeben{karte.releaseNote ? ` · ${karte.releaseNote}` : ''}
              </span>
            )}
            {(karte.marks.paused || karte.marks.onHold) && (
              <span className="flex items-center gap-0.5 rounded bg-yellow-100 px-1 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200">
                <PauseCircle className="h-3 w-3" /> {karte.marks.onHold ? 'angehalten' : 'pausiert'}
              </span>
            )}
            {karte.marks.materialMissing && (
              <span className="flex items-center gap-0.5 rounded bg-red-100 px-1 text-red-800 dark:bg-red-900 dark:text-red-200">
                <AlertTriangle className="h-3 w-3" /> Material fehlt
              </span>
            )}
            {karte.marks.supplement && <span className="rounded bg-indigo-100 px-1 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200">Nachtrag</span>}
          </div>
        </>
      )}

      <button
        type="button"
        className="mt-auto flex min-h-[1.25rem] flex-wrap items-center gap-1 text-left hover:underline"
        onPointerDown={(e) => e.stopPropagation()}
        onClick={(e) => {
          e.stopPropagation()
          onEinteilen?.()
        }}
        disabled={!onEinteilen}
      >
        {laufend.length === 0 ? (
          <span className="italic text-muted-foreground">nicht eingeteilt</span>
        ) : (
          laufend.map((a) => (
            <span key={a.id} className="flex items-center gap-0.5 rounded-full bg-primary/10 px-1.5 text-foreground" title={a.userName}>
              {a.isLead && <Star className="h-2.5 w-2.5" aria-label="führt" />}
              {a.label}
            </span>
          ))
        )}
      </button>

      {!klein && (
        <div className="flex items-center gap-2 text-muted-foreground">
          {karte.fileCount > 0 && (
            <span className="flex items-center gap-0.5">
              <FileText className="h-3 w-3" /> {karte.fileCount}
            </span>
          )}
          {karte.noteCount > 0 && (
            <span className="flex items-center gap-0.5">
              <MessageSquare className="h-3 w-3" /> {karte.noteCount}
            </span>
          )}
          {karte.responsibleName && <span className="ml-auto truncate">{karte.responsibleName}</span>}
        </div>
      )}
    </div>
  )
}
